import { Link } from "react-router-dom";
import {
  Sparkles,
  HardHat,
  Wind,
  Zap,
  Wrench,
  Briefcase,
  Stethoscope,
  UtensilsCrossed,
  Building2,
  Users,
  Landmark,
  ArrowRight,
} from "lucide-react";
import PageHero from "../components/site/PageHero";
import { Reveal } from "../components/site/Reveal";
import { Overline, PrimaryButton, GoldButton } from "../components/site/Primitives";
import { openPartners } from "../lib/site";

const INDUSTRIES = [
  { icon: Sparkles, title: "Beauty & Wellness", copy: "Our founding industry — nail specialty licensing, salon growth, and luxury services." },
  { icon: HardHat, title: "Construction", copy: "AI training and operational efficiency workshops for construction teams ready to work smarter." },
  { icon: Wind, title: "HVAC", copy: "AI training and business modernization for HVAC pros and small shops." },
  { icon: Zap, title: "Electrical", copy: "AI training and workflow tools for electrical contractors modernizing how they operate." },
  { icon: Wrench, title: "Skilled Trades", copy: "AI workshops and business efficiency training for trade shops, unions, and associations." },
  { icon: Briefcase, title: "Entrepreneurs", copy: "Business planning, branding, pricing, and AI-powered operations for founders." },
  { icon: Stethoscope, title: "Healthcare", copy: "AI training and productivity workshops for allied health teams and small practices." },
  { icon: UtensilsCrossed, title: "Hospitality", copy: "AI-enabled operations and service upskilling for hospitality teams." },
  { icon: Building2, title: "Small Business", copy: "Owners, operators, and small teams growing revenue and systems with AI." },
  { icon: Users, title: "Community Organizations", copy: "AI training workshops and workforce facilitation for community-serving organizations." },
  { icon: Landmark, title: "Government Programs", copy: "AI training, curriculum design, and workshops for public sector partners." },
];

export default function Industries() {
  return (
    <div data-testid="industries-page">
      <PageHero
        overline="Industries Served"
        title="One practice. Every industry that trains its people."
        subtitle="Nails were our starting line. Today, Nervani builds curriculum, workshops, and consulting across the trades, small business, healthcare, hospitality, community, and government."
      >
        <PrimaryButton onClick={openPartners} data-testid="industries-partner-btn">Partner With Us <ArrowRight size={16} /></PrimaryButton>
        <GoldButton as="link" to="/programs" data-testid="industries-programs-btn">See Programs</GoldButton>
      </PageHero>

      <section className="bg-brand-cream py-24 md:py-32">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {INDUSTRIES.map((it, i) => (
              <Reveal key={it.title} delay={i * 0.04}>
                <div data-testid={`industry-${it.title.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="h-full bg-white rounded-3xl border border-brand-ink/5 p-8 transition-transform duration-500 hover:-translate-y-1">
                  <div className="h-12 w-12 rounded-2xl bg-brand-blush flex items-center justify-center text-brand-rose">
                    <it.icon size={22} strokeWidth={1.6} />
                  </div>
                  <h3 className="font-serif text-2xl text-brand-ink tracking-tight mt-6 leading-tight">{it.title}</h3>
                  <p className="text-brand-ink/70 font-light text-sm mt-3 leading-relaxed">{it.copy}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-brand-blush py-24 md:py-28">
        <div className="max-w-3xl mx-auto px-6 md:px-10 text-center flex flex-col items-center">
          <Overline className="mb-5">Your industry not listed?</Overline>
          <h2 className="font-serif font-light text-brand-ink text-4xl sm:text-5xl tracking-tight">We build custom programs.</h2>
          <p className="text-brand-ink/70 font-light mt-5 max-w-lg">
            If your workforce trains people, we can help — even if you don&apos;t see
            your category above. Tell us your goal and we&apos;ll respond with an
            approach.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <PrimaryButton onClick={openPartners} data-testid="industries-cta-btn">Start the Conversation</PrimaryButton>
            <GoldButton as="link" to="/contact" data-testid="industries-contact-btn">Contact</GoldButton>
          </div>
        </div>
      </section>
    </div>
  );
}
